import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const ExpectedPoOutput = ({ preview, loading }) => {
  const [detailsOpen, setDetailsOpen] = React.useState(false);
  const groups = preview?.orderContext?.groups || [];
  const expectedCount = preview?.orderContext?.expectedPoCountForOrder;
  const totalProductCount = preview?.orderContext?.totalProductCount || 0;
  const readyProductCount = preview?.orderContext?.readyProductCount || 0;
  const incompleteProducts = preview?.orderContext?.incompleteProducts || [];
  const incompleteProductCount = preview?.orderContext?.incompleteProductCount || incompleteProducts.length;
  const actionLabel = groups.length > 0 && incompleteProducts.length > 0
    ? `View expected POs and issues${expectedCount ? ` (${expectedCount})` : ''}`
    : groups.length > 0
      ? `View expected POs${expectedCount ? ` (${expectedCount})` : ''}`
      : 'Review shipping issues';

  return (
    <>
      <Paper variant="outlined" sx={{ mt: 2, p: 2, width: '100%', bgcolor: 'grey.50' }}>
        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
          <InfoOutlinedIcon color="info" fontSize="small" />
          <Typography variant="subtitle2" fontWeight={700}>Expected PO Output</Typography>
          {loading && <CircularProgress size={14} />}
        </Box>
        {preview ? (
          <>
            <Typography variant="body2" fontWeight={700}>{preview.routeLabel}</Typography>
            <Typography variant="body2" color="text.secondary">{preview.explanation}</Typography>
            {totalProductCount > 0 && (
              <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                {expectedCount || 0} expected PO{expectedCount === 1 ? '' : 's'} · {readyProductCount} of {totalProductCount} products routing-ready
              </Typography>
            )}
            {incompleteProductCount > 0 && (
              <Alert severity="warning" icon={false} sx={{ mt: 1, py: 0, px: 1 }}>
                {incompleteProductCount} product{incompleteProductCount === 1 ? '' : 's'} still need{incompleteProductCount === 1 ? 's' : ''} shipping setup.
              </Alert>
            )}
            {(preview.warnings || []).map((warning) => (
              <Alert key={warning} severity="warning" icon={false} sx={{ mt: 1, py: 0, px: 1 }}>
                {warning}
              </Alert>
            ))}
            {(groups.length > 0 || incompleteProducts.length > 0) && (
              <Button size="small" sx={{ mt: 1, px: 0 }} onClick={() => setDetailsOpen(true)}>
                {actionLabel}
              </Button>
            )}
          </>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {loading ? 'Updating expected route…' : 'Expected route is not available yet.'}
          </Typography>
        )}
        <Typography variant="caption" color="text.secondary" display="block" mt={1}>
          Final grouping is determined when purchase orders are generated.
        </Typography>
      </Paper>

      <Dialog open={detailsOpen} onClose={() => setDetailsOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>
          Expected Purchase Orders
          {groups.length > 0 ? (
            <Typography variant="body2" color="text.secondary">
              Approximately {expectedCount || groups.length} purchase order{(expectedCount || groups.length) === 1 ? '' : 's'} for this order
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No purchase order groups can be predicted yet
            </Typography>
          )}
          {totalProductCount > 0 && (
            <Typography variant="body2" color="text.secondary">
              {readyProductCount} of {totalProductCount} products routing-ready
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          {incompleteProductCount > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {incompleteProductCount} product{incompleteProductCount === 1 ? '' : 's'} need{incompleteProductCount === 1 ? 's' : ''} shipping setup and may prevent complete purchase order generation.
            </Alert>
          )}
          <Stack spacing={1.5}>
            {groups.map((group) => {
              const currentProduct = group.products.find((item) => item.isCurrent);
              const otherCount = group.productCount - (currentProduct ? 1 : 0);
              return (
                <Paper
                  key={group.key}
                  variant="outlined"
                  sx={{ p: 1.5, borderColor: group.includesCurrentProduct ? 'primary.main' : 'divider' }}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                    <Typography variant="subtitle2" fontWeight={700}>{group.title}</Typography>
                    {group.includesCurrentProduct && <Chip size="small" color="primary" label="This product" />}
                  </Box>
                  <Typography variant="body2" fontWeight={600}>{group.routeLabel}</Typography>
                  {group.destinationLabel && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Ship To: {group.destinationLabel}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary" mt={0.5}>
                    {currentProduct
                      ? `${currentProduct.name}${currentProduct.sku ? ` (${currentProduct.sku})` : ''}${otherCount > 0 ? ` and ${otherCount} other product${otherCount === 1 ? '' : 's'}` : ''}`
                      : `${group.productCount} product${group.productCount === 1 ? '' : 's'}`}
                  </Typography>
                  {group.productCount > 1 && (
                    <Accordion disableGutters elevation={0} sx={{ mt: 0.5, bgcolor: 'transparent' }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 32, px: 0 }}>
                        <Typography variant="caption">View included products</Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ px: 0, pt: 0 }}>
                        {group.products.map((item) => (
                          <Typography key={`${group.key}-${item.orderItemId || item.productId}`} variant="caption" display="block">
                            {item.name}{item.sku ? ` · ${item.sku}` : ''}{item.isCurrent ? ' · This product' : ''}
                          </Typography>
                        ))}
                      </AccordionDetails>
                    </Accordion>
                  )}
                </Paper>
              );
            })}
          </Stack>
          {incompleteProducts.length > 0 && (
            <Box mt={2}>
              <Typography variant="subtitle2" fontWeight={700} mb={1}>
                Products needing attention
              </Typography>
              <Stack spacing={1}>
                {incompleteProducts.map((item) => (
                  <Paper
                    key={`incomplete-${item.orderItemId || item.productId}`}
                    variant="outlined"
                    sx={{ p: 1.5, borderColor: item.isCurrent ? 'primary.main' : 'warning.light' }}
                  >
                    <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                      <Typography variant="body2" fontWeight={700}>
                        {item.name}{item.sku ? ` · ${item.sku}` : ''}
                      </Typography>
                      <Stack direction="row" spacing={0.5}>
                        {item.isCurrent && <Chip size="small" color="primary" label="This product" />}
                        <Chip
                          size="small"
                          color="warning"
                          label={item.reasonCode === 'missing_shipping_record' ? 'Needs shipping' : 'Incomplete'}
                        />
                      </Stack>
                    </Box>
                    <Typography variant="body2" color="text.secondary" mt={0.5}>
                      {item.reasonLabel}
                    </Typography>
                  </Paper>
                ))}
              </Stack>
            </Box>
          )}
          <Typography variant="caption" color="text.secondary" display="block" mt={2}>
            Final grouping is determined when purchase orders are generated.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ExpectedPoOutput;
